import AddIcon from "@/assets/add.svg";
import EditIcon from "@/assets/edit.svg";
import DotsIcon from "@/assets/placeholder-dots.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ATP_WTA_ITF_ErrorMsg, ATP_WTA_ITF_Regex, bestRankingYearSchema, playerFFTranking, rankingTypes, requiredMsg, UTR_ErrorMsg, UTRRegex } from "@teamz/common";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle } from "../ui/sheet";
import { ErrorField, RootField, TextField } from "../ui/text-field";

const today = new Date().toISOString().split("T")[0];

const topVictoryDU = z.discriminatedUnion("topVictoryRankingType", [
  z.object({
    topVictoryYear: bestRankingYearSchema,
    topVictoryRankingType: z.literal("FFT"),
    topVictoryRanking: z.enum(playerFFTranking, { message: requiredMsg }),
    topVictoryRankingNumber: z.coerce.number().nullable(),
  }),
  z.object({
    topVictoryYear: bestRankingYearSchema,
    topVictoryRankingType: z.literal("UTR"),
    topVictoryRanking: z.string({ message: requiredMsg }).regex(UTRRegex, { message: UTR_ErrorMsg }),
    topVictoryRankingNumber: z.coerce.number().nullable(),
  }),
  z.object({
    topVictoryYear: bestRankingYearSchema,
    topVictoryRankingType: z.enum(["ATP", "WTA", "ITF"]),
    topVictoryRanking: z.string({ message: requiredMsg })
      .regex(ATP_WTA_ITF_Regex, { message: ATP_WTA_ITF_ErrorMsg }),
    topVictoryRankingNumber: z.coerce.number().nullable(),
  }),
], {
  errorMap: () => ({ message: requiredMsg }),
}).superRefine((data, ctx) => {
  if (data.topVictoryRanking === "T100" && !data.topVictoryRankingNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "profile:required-numbered-ranking",
      path: ["topVictoryRankingNumber"],
    });
  }
});
export type TopVictoryDU = z.infer<typeof topVictoryDU>;

function TopVictoryForm({ player, closeModal }: { player: RouterOutputs["player"]["getPlayer"]; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.player.updateTopVictory.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const topVictoryForm = useForm<TopVictoryDU>({
    resolver: zodResolver(topVictoryDU),
    // @ts-expect-error - FFTRanking is null or set, we want to force to undefined
    defaultValues: {
      topVictoryYear: player.topVictoryYear || undefined,
      topVictoryRankingType: player.topVictoryRankingType || undefined,
      topVictoryRanking: player.topVictoryRanking || undefined,
      topVictoryRankingNumber: player.topVictoryRankingNumber || null,
    },
  });

  const onSubmit = topVictoryForm.handleSubmit((data) => {
    mutate({ ...data, userId: player.userId });
  });

  const onClear = () => {
    mutate({
      userId: player.userId,
      topVictoryYear: null,
      topVictoryRankingType: null,
      topVictoryRanking: null,
      topVictoryRankingNumber: null,
    });
  };

  const victoryFormError = topVictoryForm.formState.errors;

  return (
    <>
      <SheetForm id="TopVictoryForm" onSubmit={onSubmit}>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <TextField
              type="number"
              label={t("profile:top-victory-year")}
              max={today}
              min={1950}
              {...topVictoryForm.register("topVictoryYear")}
              errorMessage={tDyn(t, victoryFormError.topVictoryYear?.message)}
            />
            <Controller
              control={topVictoryForm.control}
              name="topVictoryRankingType"
              rules={{
                onChange: (event) => {
                  if (event) {
                    topVictoryForm.setValue("topVictoryRanking", "");
                    topVictoryForm.setValue("topVictoryRankingNumber", null);
                  }
                },
              }}
              render={({ field, fieldState }) => (
                <RootField>
                  <Label>{t("common:ranking-type")}</Label>
                  <Select value={field.value || undefined} onValueChange={field.onChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {rankingTypes.map(type => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {fieldState.error && (
                    <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
                  )}
                </RootField>
              )}
            />
          </div>

          <div>
            {/* FFT Ranking */}
            <div className="flex flex-col gap-4">
              {topVictoryForm.watch("topVictoryRankingType") === "FFT"
              && (
                <>
                  <Controller
                    control={topVictoryForm.control}
                    name="topVictoryRanking"
                    rules={{
                      onChange: (event) => {
                        if (event) {
                          topVictoryForm.setValue("topVictoryRankingNumber", null);
                        }
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <RootField>
                        <Label>{t("profile:opponent-ranking")}</Label>
                        <Select value={field.value || undefined} onValueChange={field.onChange}>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {playerFFTranking.map(ranking => (
                                <SelectItem key={ranking} value={ranking}>{ranking === "T100" ? `${t("profile:numbered")} - T100` : ranking}</SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        {fieldState.error?.message && (
                          <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
                        )}
                      </RootField>
                    )}
                  />

                  {topVictoryForm.watch("topVictoryRanking") === "T100" && (
                    <TextField
                      type="number"
                      helper={t("profile:ranking-numbered-helper")}
                      prefix={<span className="text-sm font-bold">N</span>}
                      label={t("profile:numbered")}
                      max={100}
                      min={1}
                      {...topVictoryForm.register("topVictoryRankingNumber")}
                      errorMessage={tDyn(t, victoryFormError.topVictoryRankingNumber?.message)}
                    />
                  )}
                </>
              )}
            </div>

            {/* UTR Ranking */}
            {topVictoryForm.watch("topVictoryRankingType") === "UTR" && (
              <Controller
                name="topVictoryRanking"
                control={topVictoryForm.control}
                render={({ field }) => (
                  <TextField
                    helper="ex: 12.34, 1.23"
                    label={t("profile:opponent-ranking")}
                    value={field.value}
                    onChange={(event) => {
                      field.onChange(event.target.value);
                    }}
                    errorMessage={tDyn(t, victoryFormError.topVictoryRanking?.message)}
                  />
                )}
              />
            )}

            {/* ATP/WTA/ITF Ranking */}
            {
              ["ATP", "WTA", "ITF"].includes(topVictoryForm.watch("topVictoryRankingType") || "")
              && (
                <TextField
                  type="number"
                  helper={t("profile:ranking-ATP-WTA-ITF-helper")}
                  label={t("profile:opponent-ranking")}
                  max={2000}
                  min={1}
                  {...topVictoryForm.register("topVictoryRanking")}
                  errorMessage={tDyn(t, victoryFormError.topVictoryRanking?.message)}
                />
              )
            }
          </div>
        </div>

      </SheetForm>

      { (player.topVictoryYear && player.topVictoryRankingType && player.topVictoryRanking) && (
        <Button
          type="button"
          variant="ghost"
          className="text-white"
          onClick={onClear}
        >
          {t("profile:delete-top-victory")}
        </Button>
      )}

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="TopVictoryForm" loading={isPending || topVictoryForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

export function PlayerTopVictory({ player }: { player: RouterOutputs["player"]["getPlayer"] }) {
  const [modalState, updateModalState] = useState<"open" | "close">("close");
  const { t } = useTranslation(["profile"]);
  const { user } = useMe();

  return (
    <>
      <div className="flex flex-col gap-2">
        <h3 className="p-2 font-semibold leading-[1.1] text-neutral-500">{t("profile:top-victory")}</h3>
        <div className="rounded-[20px] bg-white p-2">

          {player.topVictoryYear && player.topVictoryRankingType && player.topVictoryRanking
            ? (
                <button
                  type="button"
                  onClick={() => updateModalState("open")}
                  className={cn(
                    "flex h-20 w-full items-center gap-1 rounded-xl bg-blue-600 p-1",
                    user.role === "club" && !user.isAdmin && "pointer-events-none",
                  )}
                >
                  <div className="flex h-[72px] min-w-24 items-center justify-center gap-1 rounded-lg bg-blue-500 px-3">
                    <div className="flex flex-col items-center">
                      {player.topVictoryRanking !== "T100" && <span className="text-2xl font-bold leading-none text-white">{player.topVictoryRanking}</span>}
                      {
                        (player.topVictoryRankingType === "FFT" && player.topVictoryRanking === "T100")
                        && (
                          <span className="text-2xl font-bold leading-none text-white">
                            N
                            {player.topVictoryRankingNumber}
                          </span>
                        )
                      }
                    </div>
                    <span className="text-sm font-semibold text-blue-300">{player.topVictoryRankingType}</span>
                  </div>

                  <div className="flex flex-1 items-center justify-between px-2 font-semibold">
                    <span className="text-white">{t("profile:best-victory")}</span>
                    <span className="text-sm text-blue-300">
                      (
                      {player.topVictoryYear}
                      )
                    </span>
                  </div>

                  {(user.role !== "club" || user.isAdmin) && (
                    <div className="p-1">
                      <EditIcon className="size-5 text-white" />
                    </div>
                  )}
                </button>

              )
            : (
                <button
                  onClick={() => updateModalState("open")}
                  type="button"
                  className={cn(
                    "flex h-20 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-neutral-400 bg-neutral-50",
                    user.role === "club" && !user.isAdmin && "pointer-events-none border-solid",
                  )}
                >
                  { (user.role !== "club" || user.isAdmin)
                    ? (
                        <>
                          <AddIcon className="text-orange-500" />
                          <span className="text-sm font-semibold">
                            {t("profile:add-top-victory")}
                          </span>
                        </>
                      )
                    : (
                        <DotsIcon />
                      )}
                </button>
              )}
        </div>
      </div>

      { (user.role !== "club" || user.isAdmin) && (
        <Sheet open={modalState === "open"} onOpenChange={() => updateModalState("close")}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>
                {t("profile:top-victory")}
              </SheetTitle>
            </SheetHeader>

            <TopVictoryForm player={player} closeModal={() => updateModalState("close")} />
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
